import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Table, type TableColumn } from '../components/ui/Table';
import { useLocation } from 'preact-iso';
import { api, ApiError } from '../api/client';
import { showConfirm } from '../components/Confirmer';
import { sessionSignal } from '../auth/session';
import {
  markAllNotificationsSeen,
  markNotificationSeen,
  notificationsSignal,
  unreadCountSignal,
} from '../realtime/notificationsStore';
import './IdentityPage.css';

// Native <button> that mirrors the shared Button visuals. The `Button`
// component's Props alias drops `disabled` at compile time (same workaround
// pattern as AlarmsPage / DashboardPage), so admin forms use this wrapper
// to keep the busy-state affordance intact.
type BtnVariant = 'primary' | 'secondary' | 'ghost';
interface BtnProps {
  variant?: BtnVariant;
  size?: 'sm' | 'md';
  onClick?: () => void;
  disabled?: boolean;
  children?: ComponentChildren;
}
function Btn({ variant = 'secondary', size = 'md', onClick, disabled, children }: BtnProps) {
  const classes = ['hv-btn', `hv-btn--${variant}`, `hv-btn--${size}`, disabled ? 'hv-btn--disabled' : '']
    .filter(Boolean).join(' ');
  return <button type="button" class={classes} onClick={onClick} disabled={disabled}>{children}</button>;
}

// --- Wire shapes (mirror C# DTOs) ------------------------------------------
interface LinkedIdentityDto {
  providerId: string;
  subject: string;
  providerUsername: string | null;
}

interface UserListEntry {
  id: string;
  displayName: string;
  email: string | null;
  isActive: boolean;
  groupIds: string[];
  dataScopeIds: string[];
  linkedIdentity: LinkedIdentityDto | null;
}
interface ListUsersResponse { users: UserListEntry[] }

interface GroupListEntry {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  permissionKeys: string[];
  dataScopeIds: string[];
  memberCount: number;
}
interface ListGroupsResponse { groups: GroupListEntry[] }

interface DataScopeDto { id: string; name: string; description: string | null }
interface ListScopesResponse { scopes: DataScopeDto[] }
interface ListPermissionsResponse { permissions: string[] }

type TabId = 'users' | 'groups' | 'mentions';

/**
 * Admin surface for users, groups, permissions and data-scopes.
 * Route: /settings/identity. Gated by users.admin OR groups.admin.
 *
 * State model: three lists (users, groups, scopes) plus the permissions
 * catalogue are fetched once on mount and cached in local state; every mutation
 * refetches the affected list so we stay authoritative without pulling in a
 * separate cache library.
 */
export function IdentityPage() {
  const permissions = sessionSignal.value.user?.permissions ?? [];
  const canAdminUsers = permissions.includes('users.admin');
  const canAdminGroups = permissions.includes('groups.admin');

  const [tab, setTab] = useState<TabId>(canAdminUsers ? 'users' : (canAdminGroups ? 'groups' : 'mentions'));

  const [users, setUsers] = useState<UserListEntry[]>([]);
  const [groups, setGroups] = useState<GroupListEntry[]>([]);
  const [scopes, setScopes] = useState<DataScopeDto[]>([]);
  const [permissionCatalog, setPermissionCatalog] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const scopeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of scopes) map.set(s.id, s.name);
    return map;
  }, [scopes]);

  const groupNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) map.set(g.id, g.name);
    return map;
  }, [groups]);

  async function reloadUsers() {
    const res = await api.get<ListUsersResponse>('/api/identity/users');
    setUsers(res.users);
  }
  async function reloadGroups() {
    const res = await api.get<ListGroupsResponse>('/api/identity/groups');
    setGroups(res.groups);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const reqs: Promise<unknown>[] = [];
        if (canAdminUsers) reqs.push(reloadUsers());
        if (canAdminGroups) reqs.push(reloadGroups());
        // Scopes + permissions are the shared reference lists — required by
        // either tab, so load them once regardless of which admin role we hold.
        reqs.push(
          api.get<ListScopesResponse>('/api/identity/scopes').then((r) => setScopes(r.scopes)),
          api.get<ListPermissionsResponse>('/api/identity/permissions').then((r) => setPermissionCatalog(r.permissions)),
        );
        await Promise.all(reqs);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError && e.status === 403
            ? 'Keine Berechtigung für diese Ansicht.'
            : 'Fehler beim Laden der Daten.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedUser = useMemo(() => users.find((u) => u.id === selectedUserId) ?? null, [users, selectedUserId]);
  const selectedGroup = useMemo(() => groups.find((g) => g.id === selectedGroupId) ?? null, [groups, selectedGroupId]);

  return (
    <AppShell title="Rollen & Rechte">
      <div class="hv-identity">
        <div class="hv-identity__tabs" role="tablist">
          {canAdminUsers && (
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'users'}
              class={`hv-identity__tab${tab === 'users' ? ' hv-identity__tab--active' : ''}`}
              onClick={() => setTab('users')}
            >Benutzer</button>
          )}
          {canAdminGroups && (
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'groups'}
              class={`hv-identity__tab${tab === 'groups' ? ' hv-identity__tab--active' : ''}`}
              onClick={() => setTab('groups')}
            >Gruppen</button>
          )}
          {/* Erwähnungen: always available — this is the user's own inbox,
              not an admin surface. Sits after the admin tabs so power users
              still land on their usual view. */}
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'mentions'}
            class={`hv-identity__tab${tab === 'mentions' ? ' hv-identity__tab--active' : ''}`}
            onClick={() => setTab('mentions')}
          >
            Erwähnungen{unreadCountSignal.value > 0 ? ` (${unreadCountSignal.value})` : ''}
          </button>
        </div>

        {loading && <p class="muted">Lade…</p>}
        {error && <p class="hv-identity__error">{error}</p>}

        {!loading && !error && tab === 'users' && canAdminUsers && (
          <UsersTab
            users={users}
            groups={groups}
            groupNameById={groupNameById}
            scopeNameById={scopeNameById}
            selected={selectedUser}
            onSelect={setSelectedUserId}
            onReload={reloadUsers}
          />
        )}
        {!loading && !error && tab === 'groups' && canAdminGroups && (
          <GroupsTab
            groups={groups}
            users={users}
            scopes={scopes}
            permissionCatalog={permissionCatalog}
            selected={selectedGroup}
            onSelect={setSelectedGroupId}
            onReload={async () => { await reloadGroups(); if (canAdminUsers) await reloadUsers(); }}
          />
        )}
        {tab === 'mentions' && <MentionsTab />}
      </div>
    </AppShell>
  );
}

// --- Users tab -------------------------------------------------------------

interface UsersTabProps {
  users: UserListEntry[];
  groups: GroupListEntry[];
  groupNameById: Map<string, string>;
  scopeNameById: Map<string, string>;
  selected: UserListEntry | null;
  onSelect: (id: string | null) => void;
  onReload: () => Promise<void>;
}

function UsersTab({ users, groups, groupNameById, scopeNameById, selected, onSelect, onReload }: UsersTabProps) {
  const [creating, setCreating] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function createUser() {
    if (!newDisplayName.trim()) {
      setSaveError('Anzeigename darf nicht leer sein.');
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      await api.post('/api/identity/users', {
        displayName: newDisplayName.trim(),
        email: newEmail.trim() || null,
      });
      setNewDisplayName('');
      setNewEmail('');
      setCreating(false);
      await onReload();
    } catch (e) {
      setSaveError(mapError(e, 'Anlegen fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  }

  const columns: TableColumn<UserListEntry>[] = [
    { key: 'name', header: 'Anzeigename', render: (u) => u.displayName },
    { key: 'email', header: 'E-Mail', render: (u) => u.email ?? <span class="muted">—</span> },
    {
      key: 'active',
      header: 'Status',
      width: '110px',
      render: (u) => (
        <StatusBadge tone={u.isActive ? 'ok' : 'neutral'}>
          {u.isActive ? 'Aktiv' : 'Deaktiviert'}
        </StatusBadge>
      ),
    },
    {
      key: 'groups',
      header: 'Gruppen',
      render: (u) => (
        <span class="hv-identity__chip-row">
          {u.groupIds.length === 0 && <span class="muted">—</span>}
          {u.groupIds.map((gid) => (
            <span key={gid} class="hv-identity__chip">{groupNameById.get(gid) ?? gid}</span>
          ))}
        </span>
      ),
    },
  ];

  return (
    <div class="hv-identity__split">
      <div class="hv-identity__list">
        <div class="hv-identity__toolbar">
          <Btn variant="primary" size="sm" onClick={() => setCreating(true)}>Neuer Benutzer</Btn>
        </div>
        {creating && (
          <div class="hv-identity__form">
            <label class="hv-identity__field">
              <span>Anzeigename</span>
              <input
                type="text"
                value={newDisplayName}
                onInput={(e) => setNewDisplayName((e.currentTarget as HTMLInputElement).value)}
              />
            </label>
            <label class="hv-identity__field">
              <span>E-Mail</span>
              <input
                type="email"
                value={newEmail}
                onInput={(e) => setNewEmail((e.currentTarget as HTMLInputElement).value)}
              />
            </label>
            {saveError && <p class="hv-identity__error">{saveError}</p>}
            <div class="hv-identity__form-actions">
              <Btn variant="ghost" size="sm" onClick={() => { setCreating(false); setSaveError(null); }} disabled={busy}>Abbrechen</Btn>
              <Btn variant="primary" size="sm" onClick={() => void createUser()} disabled={busy}>Speichern</Btn>
            </div>
          </div>
        )}
        <Table
          columns={columns}
          rows={users}
          rowKey={(u) => u.id}
          onRowClick={(u) => onSelect(u.id)}
          emptyLabel="Keine Benutzer angelegt."
        />
      </div>

      <div class="hv-identity__detail">
        {selected ? (
          <UserDetail
            key={selected.id}
            user={selected}
            groups={groups}
            scopeNameById={scopeNameById}
            onReload={onReload}
          />
        ) : (
          <div class="hv-identity__placeholder">Benutzer aus der Liste wählen.</div>
        )}
      </div>
    </div>
  );
}

interface UserDetailProps {
  user: UserListEntry;
  groups: GroupListEntry[];
  scopeNameById: Map<string, string>;
  onReload: () => Promise<void>;
}

function UserDetail({ user, groups, scopeNameById, onReload }: UserDetailProps) {
  const [displayName, setDisplayName] = useState(user.displayName);
  const [isActive, setIsActive] = useState(user.isActive);
  const [groupIds, setGroupIds] = useState<Set<string>>(new Set(user.groupIds));
  const [providerId, setProviderId] = useState(user.linkedIdentity?.providerId ?? 'keycloak');
  const [subject, setSubject] = useState(user.linkedIdentity?.subject ?? '');
  const [providerUsername, setProviderUsername] = useState(user.linkedIdentity?.providerUsername ?? '');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const dirty =
    displayName !== user.displayName ||
    isActive !== user.isActive ||
    !setsEqual(groupIds, new Set(user.groupIds));

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      if (displayName !== user.displayName || isActive !== user.isActive) {
        await api.patch(`/api/identity/users/${user.id}`, {
          displayName: displayName !== user.displayName ? displayName : null,
          isActive: isActive !== user.isActive ? isActive : null,
        });
      }
      if (!setsEqual(groupIds, new Set(user.groupIds))) {
        await api.post(`/api/identity/users/${user.id}/groups`, {
          groupIds: Array.from(groupIds),
        });
      }
      await onReload();
      setMessage('Gespeichert.');
    } catch (e) {
      setMessage(mapError(e, 'Speichern fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  }

  async function saveIdentity() {
    if (!subject.trim() || !providerId.trim()) {
      setMessage('Provider und Subject sind erforderlich.');
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await api.post(`/api/identity/users/${user.id}/link-identity`, {
        providerId: providerId.trim(),
        subject: subject.trim(),
        providerUsername: providerUsername.trim() || null,
      });
      await onReload();
      setMessage('Identität verknüpft.');
    } catch (e) {
      setMessage(mapError(e, 'Verknüpfen fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  }

  const effectiveScopes = useMemo(() => {
    const scopeIds = new Set<string>();
    for (const gid of groupIds) {
      const g = groups.find((x) => x.id === gid);
      if (g) for (const s of g.dataScopeIds) scopeIds.add(s);
    }
    return Array.from(scopeIds);
  }, [groupIds, groups]);

  return (
    <div class="hv-identity__panel">
      <h3 class="hv-identity__panel-title">{user.displayName}</h3>
      <code class="mono muted">{user.id}</code>

      <label class="hv-identity__field">
        <span>Anzeigename</span>
        <input
          type="text"
          value={displayName}
          onInput={(e) => setDisplayName((e.currentTarget as HTMLInputElement).value)}
        />
      </label>

      <label class="hv-identity__toggle">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive((e.currentTarget as HTMLInputElement).checked)}
        />
        <span>Aktiv</span>
      </label>

      <div class="hv-identity__section">
        <h4>Gruppen</h4>
        <div class="hv-identity__checkbox-list">
          {groups.map((g) => (
            <label key={g.id} class="hv-identity__checkbox">
              <input
                type="checkbox"
                checked={groupIds.has(g.id)}
                onChange={(e) => {
                  const next = new Set(groupIds);
                  if ((e.currentTarget as HTMLInputElement).checked) next.add(g.id);
                  else next.delete(g.id);
                  setGroupIds(next);
                }}
              />
              <span>{g.name}</span>
            </label>
          ))}
          {groups.length === 0 && <span class="muted">Keine Gruppen definiert.</span>}
        </div>
      </div>

      <div class="hv-identity__section">
        <h4>Effektive Datenscopes</h4>
        <span class="hv-identity__chip-row">
          {effectiveScopes.length === 0 && <span class="muted">Keine</span>}
          {effectiveScopes.map((sid) => (
            <span key={sid} class="hv-identity__chip">{scopeNameById.get(sid) ?? sid}</span>
          ))}
        </span>
      </div>

      <div class="hv-identity__form-actions">
        <Btn variant="primary" size="sm" onClick={() => void save()} disabled={busy || !dirty}>Speichern</Btn>
      </div>

      <div class="hv-identity__section">
        <h4>Verknüpfte Identität (OIDC)</h4>
        <label class="hv-identity__field">
          <span>Provider</span>
          <input type="text" value={providerId} onInput={(e) => setProviderId((e.currentTarget as HTMLInputElement).value)} />
        </label>
        <label class="hv-identity__field">
          <span>Subject</span>
          <input type="text" value={subject} onInput={(e) => setSubject((e.currentTarget as HTMLInputElement).value)} />
        </label>
        <label class="hv-identity__field">
          <span>Provider-Benutzername</span>
          <input type="text" value={providerUsername} onInput={(e) => setProviderUsername((e.currentTarget as HTMLInputElement).value)} />
        </label>
        <div class="hv-identity__form-actions">
          <Btn variant="secondary" size="sm" onClick={() => void saveIdentity()} disabled={busy}>Identität verknüpfen</Btn>
        </div>
      </div>

      {message && <p class="hv-identity__message">{message}</p>}
    </div>
  );
}

// --- Groups tab ------------------------------------------------------------

interface GroupsTabProps {
  groups: GroupListEntry[];
  users: UserListEntry[];
  scopes: DataScopeDto[];
  permissionCatalog: string[];
  selected: GroupListEntry | null;
  onSelect: (id: string | null) => void;
  onReload: () => Promise<void>;
}

function GroupsTab({ groups, users, scopes, permissionCatalog, selected, onSelect, onReload }: GroupsTabProps) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function createGroup() {
    if (!newName.trim()) {
      setSaveError('Name darf nicht leer sein.');
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      await api.post('/api/identity/groups', {
        name: newName.trim(),
        description: newDescription.trim() || null,
      });
      setNewName('');
      setNewDescription('');
      setCreating(false);
      await onReload();
    } catch (e) {
      setSaveError(mapError(e, 'Anlegen fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  }

  const columns: TableColumn<GroupListEntry>[] = [
    { key: 'name', header: 'Name', render: (g) => g.name },
    {
      key: 'default',
      header: '',
      width: '110px',
      render: (g) => g.isDefault ? <StatusBadge tone="neutral">Default</StatusBadge> : null,
    },
    { key: 'permissions', header: 'Rechte', render: (g) => <span class="muted">{g.permissionKeys.length}</span>, width: '80px' },
    { key: 'scopes', header: 'Scopes', render: (g) => <span class="muted">{g.dataScopeIds.length}</span>, width: '80px' },
    { key: 'members', header: 'Mitglieder', render: (g) => <span class="muted">{g.memberCount}</span>, width: '100px' },
  ];

  return (
    <div class="hv-identity__split">
      <div class="hv-identity__list">
        <div class="hv-identity__toolbar">
          <Btn variant="primary" size="sm" onClick={() => setCreating(true)}>Neue Gruppe</Btn>
        </div>
        {creating && (
          <div class="hv-identity__form">
            <label class="hv-identity__field">
              <span>Name</span>
              <input
                type="text"
                value={newName}
                onInput={(e) => setNewName((e.currentTarget as HTMLInputElement).value)}
              />
            </label>
            <label class="hv-identity__field">
              <span>Beschreibung</span>
              <input
                type="text"
                value={newDescription}
                onInput={(e) => setNewDescription((e.currentTarget as HTMLInputElement).value)}
              />
            </label>
            {saveError && <p class="hv-identity__error">{saveError}</p>}
            <div class="hv-identity__form-actions">
              <Btn variant="ghost" size="sm" onClick={() => { setCreating(false); setSaveError(null); }} disabled={busy}>Abbrechen</Btn>
              <Btn variant="primary" size="sm" onClick={() => void createGroup()} disabled={busy}>Speichern</Btn>
            </div>
          </div>
        )}
        <Table
          columns={columns}
          rows={groups}
          rowKey={(g) => g.id}
          onRowClick={(g) => onSelect(g.id)}
          emptyLabel="Keine Gruppen angelegt."
        />
      </div>

      <div class="hv-identity__detail">
        {selected ? (
          <GroupDetail
            key={selected.id}
            group={selected}
            users={users}
            scopes={scopes}
            permissionCatalog={permissionCatalog}
            onReload={onReload}
            onDeleted={() => { onSelect(null); void onReload(); }}
          />
        ) : (
          <div class="hv-identity__placeholder">Gruppe aus der Liste wählen.</div>
        )}
      </div>
    </div>
  );
}

interface GroupDetailProps {
  group: GroupListEntry;
  users: UserListEntry[];
  scopes: DataScopeDto[];
  permissionCatalog: string[];
  onReload: () => Promise<void>;
  onDeleted: () => void;
}

function GroupDetail({ group, users, scopes, permissionCatalog, onReload, onDeleted }: GroupDetailProps) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? '');
  const [permissionKeys, setPermissionKeys] = useState<Set<string>>(new Set(group.permissionKeys));
  const [dataScopeIds, setDataScopeIds] = useState<Set<string>>(new Set(group.dataScopeIds));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const members = useMemo(
    () => users.filter((u) => u.groupIds.includes(group.id)),
    [users, group.id],
  );

  const metaDirty = name !== group.name || (description || null) !== (group.description ?? null);
  const permsDirty = !setsEqual(permissionKeys, new Set(group.permissionKeys));
  const scopesDirty = !setsEqual(dataScopeIds, new Set(group.dataScopeIds));
  const dirty = metaDirty || permsDirty || scopesDirty;

  async function save() {
    if (!name.trim()) {
      setMessage('Name darf nicht leer sein.');
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      if (metaDirty) {
        await api.patch(`/api/identity/groups/${group.id}`, {
          name: name !== group.name ? name.trim() : null,
          description: (description || null) !== (group.description ?? null) ? (description.trim() || null) : null,
        });
      }
      if (permsDirty) {
        await api.post(`/api/identity/groups/${group.id}/permissions`, {
          permissions: Array.from(permissionKeys),
        });
      }
      if (scopesDirty) {
        await api.post(`/api/identity/groups/${group.id}/scopes`, {
          dataScopeIds: Array.from(dataScopeIds),
        });
      }
      await onReload();
      setMessage('Gespeichert.');
    } catch (e) {
      setMessage(mapError(e, 'Speichern fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  }

  async function deleteGroup() {
    if (group.isDefault) return;
    const ok = await showConfirm({
      title: 'Gruppe löschen?',
      message: `"${group.name}" wird entfernt.`,
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.delete(`/api/identity/groups/${group.id}`);
      onDeleted();
    } catch (e) {
      setMessage(mapError(e, 'Löschen fehlgeschlagen.'));
      setBusy(false);
    }
  }

  return (
    <div class="hv-identity__panel">
      <h3 class="hv-identity__panel-title">{group.name}</h3>
      <code class="mono muted">{group.id}</code>

      <label class="hv-identity__field">
        <span>Name</span>
        <input
          type="text"
          value={name}
          onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
        />
      </label>
      <label class="hv-identity__field">
        <span>Beschreibung</span>
        <input
          type="text"
          value={description}
          onInput={(e) => setDescription((e.currentTarget as HTMLInputElement).value)}
        />
      </label>

      <div class="hv-identity__section">
        <h4>Rechte</h4>
        <div class="hv-identity__checkbox-list">
          {permissionCatalog.map((p) => (
            <label key={p} class="hv-identity__checkbox">
              <input
                type="checkbox"
                checked={permissionKeys.has(p)}
                onChange={(e) => {
                  const next = new Set(permissionKeys);
                  if ((e.currentTarget as HTMLInputElement).checked) next.add(p);
                  else next.delete(p);
                  setPermissionKeys(next);
                }}
              />
              <code class="mono">{p}</code>
            </label>
          ))}
          {permissionCatalog.length === 0 && <span class="muted">Katalog leer.</span>}
        </div>
      </div>

      <div class="hv-identity__section">
        <h4>Datenscopes</h4>
        <div class="hv-identity__checkbox-list">
          {scopes.map((s) => (
            <label key={s.id} class="hv-identity__checkbox">
              <input
                type="checkbox"
                checked={dataScopeIds.has(s.id)}
                onChange={(e) => {
                  const next = new Set(dataScopeIds);
                  if ((e.currentTarget as HTMLInputElement).checked) next.add(s.id);
                  else next.delete(s.id);
                  setDataScopeIds(next);
                }}
              />
              <span>{s.name}</span>
            </label>
          ))}
          {scopes.length === 0 && <span class="muted">Keine Datenscopes definiert.</span>}
        </div>
      </div>

      <div class="hv-identity__form-actions">
        <Btn variant="primary" size="sm" onClick={() => void save()} disabled={busy || !dirty}>Speichern</Btn>
        {!group.isDefault && (
          <Btn variant="ghost" size="sm" onClick={() => void deleteGroup()} disabled={busy}>Löschen</Btn>
        )}
      </div>

      <div class="hv-identity__section">
        <h4>Mitglieder ({members.length})</h4>
        {members.length === 0 ? (
          <span class="muted">Keine Mitglieder.</span>
        ) : (
          <ul class="hv-identity__member-list">
            {members.map((u) => (
              <li key={u.id}>
                <span>{u.displayName}</span>
                {u.email && <span class="muted"> — {u.email}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {message && <p class="hv-identity__message">{message}</p>}
    </div>
  );
}

// --- Mentions inbox tab ---------------------------------------------------

/**
 * Personal mention inbox — shows every MentionNotification for the current
 * user, seen or unseen. Rides on the same notificationsStore the top-bar
 * bell reads from, so opening this tab keeps everything else in sync.
 */
function MentionsTab() {
  const location = useLocation();
  const list = notificationsSignal.value;
  const unread = unreadCountSignal.value;

  const openItem = (n: typeof list[number]) => {
    if (!n.seenAt) void markNotificationSeen(n.id);
    if (n.sourceKind === 'ChartAnnotation') {
      location.route(`/plants/${n.plantId}/charts?annotation=${n.sourceId}`);
    }
  };

  return (
    <div class="hv-identity__mentions">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
        <strong>Deine Erwähnungen</strong>
        {unread > 0 && (
          <Btn variant="ghost" size="sm" onClick={() => void markAllNotificationsSeen()}>
            Alle als gelesen markieren
          </Btn>
        )}
      </div>
      {list.length === 0 ? (
        <p class="muted">Noch keine Erwähnungen.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          {list.map((n) => (
            <li
              key={n.id}
              style={{
                padding: 'var(--space-2)',
                border: '1px solid var(--border-subtle)',
                borderLeft: n.seenAt ? '3px solid transparent' : '3px solid var(--status-warn)',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 'var(--space-2)',
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--fs-sm)' }}>{n.previewText || '(kein Vorschautext)'}</div>
                <div class="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 4 }}>
                  {new Date(n.createdAt).toLocaleString()} · {n.sourceKind === 'ChartAnnotation' ? 'Chart-Notiz' : n.sourceKind}
                  {n.seenAt ? ` · gesehen ${new Date(n.seenAt).toLocaleString()}` : ' · ungelesen'}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Btn variant="secondary" size="sm" onClick={() => openItem(n)}>Öffnen</Btn>
                {!n.seenAt && (
                  <Btn variant="ghost" size="sm" onClick={() => void markNotificationSeen(n.id)}>
                    Gesehen
                  </Btn>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- Helpers ---------------------------------------------------------------

function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function mapError(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return 'Keine Berechtigung.';
    if (e.status === 404) return 'Datensatz nicht gefunden.';
    if (e.status === 409) return typeof (e.body as { error?: string })?.error === 'string'
      ? (e.body as { error: string }).error
      : 'Konflikt (bereits vorhanden).';
    if (e.status === 400 && typeof (e.body as { error?: string })?.error === 'string') {
      return (e.body as { error: string }).error;
    }
  }
  return fallback;
}
